import { inject } from 'vue'
import { patch } from '@rails/request.js'
import { useSaveStatus } from './useSaveStatus'
import { t } from '@/libs/i18n'

export function useBlockUpdate(block, options = {}) {
  const { setStatus, setIsSaving, setStatusToLastSaved, setErrors, resetErrors } = useSaveStatus()
  const showErrorStatus = options.showErrorStatus ?? true

  const updateSlides = inject('updateSlides')

  let latestRequest = 0

  const updateBlock = async (updatePath, body, headers = { 'Content-Type': 'application/json' }, responseKind = 'json') => {
    const requestId = ++latestRequest
    const startTime = Date.now()

    setStatus(t('slides_editor.status_messages.saving'))
    setIsSaving(true)

    try {
      const response = await patch(updatePath, {
        body,
        headers,
        responseKind
      })

      // A superseded save's response describes state a newer request has since replaced —
      // applying it would rewind slides and ids. The newest request owns the outcome.
      if (requestId !== latestRequest) return null

      if (response.ok) {
        resetErrors()
        return handleSuccessfulUpdate(response, startTime)
      } else {
        handleUpdateError(await response.json)
      }
    } catch (error) {
      handleUpdateError(error)
      throw error
    }
  }

  const handleSuccessfulUpdate = async (response, startTime) => {
    const parsedResponse = await response.json

    updateSlides(parsedResponse.slide)

    const elapsedTime = Date.now() - startTime
    const remainingTime = Math.max(0, 1000 - elapsedTime)

    setTimeout(setStatusToLastSaved, remainingTime)
    setIsSaving(false)

    return parsedResponse
  }

  const handleUpdateError = (error) => {
    console.error('Error updating block:', error)

    if (showErrorStatus) {
      setStatus(t('slides_editor.status_messages.error_saving'))
    } else {
      setStatus('')
    }

    setErrors(error.errors)
    setIsSaving(false)
  }

  const makeParams = (content, field) => {
    // Sortable questions aren't fully supported yet: saving their full content breaks
    // them, so send only the new position. The id and variant_type have to travel with
    // it — without the id the server reads the entry as a new question, and without
    // variant_type legacy's nested attributes read it as a delete.
    if (content.questions_attributes) {
      content.questions_attributes = content.questions_attributes.map((question) => {
        if (question.variant_name.toLowerCase() === 'sortable') {
          return {
            id: question.id,
            variant_type: question.variant_type,
            position: question.position
          }
        }

        return question
      })
    }

    const parts = field.split('.')

    let params = {}
    let current = params

    parts.forEach((key, index) => {
      if (index === parts.length - 1) {
        current[key] = content
      } else {
        current[key] = {}
        current = current[key]
      }
    })

    return { block: params }
  }

  const onBlockUpdate = async (content, field) => {
    if (content instanceof File) {
      const data = new FormData()
      data.append(`block[${field}]`, content)

      return updateBlock(block.update_path, data, {})
    } else {
      return updateBlock(block.update_path, makeParams(content, field))
    }
  }

  const onBlockUpdateMany = async (attributes) => {
    return updateBlock(block.update_path, { block: attributes })
  }

  return {
    onBlockUpdate,
    onBlockUpdateMany
  }
}
